/**
 * departments.ts — dual-path bridge for the v7.42 /departments router
 * (Module 3 HR Core audit-gap close).
 *
 * The backend's `DepartmentOut` shape matches the frontend `Department`
 * type column-for-column (id, name, description, color, head_id,
 * admin_id, created_at), so a clean swap is possible — no schema
 * negotiation, no shape conversion.
 *
 * Helpers:
 *   - fetchDepartments(workspaceId)      list helper, used by useDepartments
 *
 * Why these (and only these):
 *   The cross-cutting consumer is `src/hooks/useDepartments.ts` which
 *   feeds every dropdown / org-chart / dept-scoped filter in the app.
 *   Bridging that single hook upgrades dozens of pages to the FastAPI
 *   path without touching each one. Single point of swap = single
 *   point of rollback.
 *
 *   `EmployeesPage` and `useEmployees.ts` are NOT bridged in this
 *   commit. EmployeesPage reads richer columns (position, phone,
 *   hire_date, salary) than the backend's auth-scoping subset
 *   carries; bridging it would render the page with missing fields.
 *   The router docstring documents this — those columns get added
 *   when a backend feature actually drives them, then both layers
 *   migrate together.
 */

import { api, apiConfigured } from './apiClient';
import { supabase } from './supabase';
import type { Department } from '../types';

// ─── fetchDepartments ───────────────────────────────────────────────────────


/**
 * Fetch all departments for a workspace, alphabetically by name.
 *
 * Backend path: GET /api/v1/departments?page=1&page_size=500.
 * Supabase fallback: select * from departments order by name.
 *
 * The 500-row page-size matches the v5 frontend's expectation that
 * a workspace has "tens of departments at most" and never paginates.
 * If a workspace ever grows beyond that, the hook layer can switch to
 * incremental fetch — but the soft cap on the API itself is generous.
 *
 * Returns [] on total failure (never throws). Logs a warn so the
 * problem doesn't disappear silently.
 */
export async function fetchDepartments(workspaceId: string): Promise<Department[]> {
  if (apiConfigured()) {
    const res = await api.get<{ items: Department[] }>(
      '/departments?page=1&page_size=500',
    );
    if (res.ok && res.data && Array.isArray(res.data.items)) {
      return res.data.items;
    }
    // Fall through to Supabase on any error.
  }

  try {
    const { data, error } = await supabase
      .from('departments')
      .select('id, name, description, color, head_id, admin_id, created_at')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data ?? []) as Department[];
  } catch (err) {
    console.warn('[fetchDepartments] supabase fallback failed', err);
    return [];
  }
}
